import { useParams } from 'react-router-dom';
import { Card, Spinner, Button } from '@/components/ui';
import { PageHeader } from '@/components/layout';
import { useTechnicalAssessmentDetail } from '@/api/useTechnicalAssessments';
import { ROUTES } from '@/constants/routes';
import styles from './TechnicalAssessment.module.css';

export function TechnicalAssessmentView() {
    const { id } = useParams();
    const { data: assessment, isLoading } = useTechnicalAssessmentDetail(id || '');

    if (isLoading) {
        return <div className="flex justify-center p-8"><Spinner size="lg" /></div>;
    }

    if (!assessment) {
        return <div className={styles.container}>Evaluación no encontrada</div>;
    }

    // Calcs
    const totalEquipment = assessment.equipment_evaluations?.length || 0;
    const operativeCount = assessment.equipment_evaluations?.filter((e: any) => e.is_operative).length || 0;
    const inoperativeCount = totalEquipment - operativeCount;

    const priorityCounts = {
        high: assessment.equipment_evaluations?.filter((e: any) => e.priority === 'high').length || 0,
        medium: assessment.equipment_evaluations?.filter((e: any) => e.priority === 'medium').length || 0,
        low: assessment.equipment_evaluations?.filter((e: any) => e.priority === 'low').length || 0,
    };

    const totalInvestment = assessment.equipment_evaluations?.reduce((acc: number, e: any) => acc + (e.total_estimated_cost || 0), 0) || 0;

    return (
        <div className={styles.container}>
            <PageHeader
                title={`Evaluación Técnica #${assessment.report_number || assessment.id}`}
                subtitle="Vista Detallada"
                backPath={ROUTES.reportsLibrary}
                actions={
                    <div className="flex gap-2">
                        <Button
                            variant="primary"
                            onClick={() => window.print()}
                        >
                            <i className="fa-solid fa-file-pdf mr-2"></i>
                            Descargar PDF
                        </Button>
                    </div>
                }
            />

            <Card className={styles.readOnlyCard} variant="full">
                {/* Executive Summary Grid - No internal borders, clean background */}
                <div className={styles.summaryGrid}>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Total Equipos</span>
                        <span className={styles.summaryValue}>{totalEquipment}</span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Inversión Est.</span>
                        <span className={styles.summaryValue} style={{ color: 'var(--color-primary)' }}>
                            ${totalInvestment.toLocaleString()}
                        </span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Prioridad Alta</span>
                        <span className={styles.summaryValue} style={{ color: priorityCounts.high > 0 ? 'var(--color-error)' : 'var(--text-muted)' }}>
                            {priorityCounts.high}
                        </span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Inoperativos</span>
                        <span className={styles.summaryValue} style={{ color: inoperativeCount > 0 ? 'var(--color-error)' : 'var(--text-muted)' }}>
                            {inoperativeCount}
                        </span>
                    </div>
                </div>

                <div className={styles.summaryInfo}>
                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Fecha de Emisión</span>
                        <span className={styles.infoValue}>{assessment.date}</span>
                    </div>
                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Asunto</span>
                        <span className={styles.infoValue}>{assessment.title}</span>
                    </div>
                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Técnico Evaluador</span>
                        <span className={styles.infoValue}>{assessment.technician_name}</span>
                    </div>
                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Solicitado Por</span>
                        <span className={styles.infoValue}>{assessment.recipient_name || 'N/A'}</span>
                    </div>
                </div>
            </Card>

            <h3 className={styles.sectionHeader}>Evaluación de Equipos</h3>

            <div className={styles.equipmentGrid}>
                {assessment.equipment_evaluations?.map((equip: any, index: number) => (
                    <div key={index} className={styles.readOnlyEquipCard}>
                        <div className={styles.equipHeader}>
                            <h4 className={styles.equipTitle}>{equip.equipment_name || equip.equipment_code || `Equipo ${equip.equipment_id}`}</h4>
                            <span className={`${styles.statusBadge} ${equip.is_operative ? styles.operative : styles.inoperative}`}>
                                {equip.is_operative ? 'OPERATIVO' : 'INOPERATIVO'}
                            </span>
                        </div>

                        <div className={styles.equipMeta}>
                            <div className={styles.infoRow}>
                                <span className={styles.infoLabel}>Condición</span>
                                <span className={styles.infoValue}>
                                    {equip.condition === 'excellent' ? 'Excelente' : equip.condition === 'good' ? 'Bueno' : equip.condition === 'fair' ? 'Regular' : 'Crítico'}
                                </span>
                            </div>
                            <div className={styles.infoRow}>
                                <span className={styles.infoLabel}>Costo Est.</span>
                                <span className={styles.infoValue}>${(equip.total_estimated_cost || 0).toLocaleString()}</span>
                            </div>
                            <div className={styles.infoRow}>
                                <span className={styles.infoLabel}>Prioridad</span>
                                <span className={styles[`priority-${equip.priority}`]}>{equip.priority?.toUpperCase()}</span>
                            </div>
                            {/* Optional 4th column can be empty or used for something else */}
                        </div>

                        {equip.findings && (typeof equip.findings === 'string' ? JSON.parse(equip.findings.replace(/'/g, '"')) : equip.findings).length > 0 ? (
                            <div className={styles.readOnlyFindingsList}>
                                <div className={styles.findingHeader}>
                                    <span className={styles.infoLabel} style={{ marginBottom: 0 }}>Hallazgos y Reparaciones</span>
                                </div>
                                <ul className={styles.findingsUl}>
                                    {(typeof equip.findings === 'string' ? JSON.parse(equip.findings.replace(/'/g, '"')) : equip.findings).map((f: any, i: number) => (
                                        <li key={i} className={styles.findingItem}>
                                            <div className={styles.findingHeader}>
                                                <span className={styles.systemName}>{f.system_name}</span>
                                                <span className={styles.findingCost}>${(f.estimated_cost || 0).toLocaleString()}</span>
                                            </div>
                                            <p className={styles.findingDesc}>{f.description}</p>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className={styles.readOnlyFindingsList}>
                                <p className={styles.findingDesc} style={{ fontStyle: 'italic', paddingLeft: 'var(--spacing-sm)' }}>Sin hallazgos reportados</p>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <Card className={styles.readOnlyCard} variant="full">
                {assessment.summary && (
                    <div className={styles.conclusionBlock}>
                        <h5>Resumen Ejecutivo</h5>
                        <p>{assessment.summary}</p>
                    </div>
                )}
                {assessment.recommendations && (
                    <div className={styles.conclusionBlock}>
                        <h5>Acciones Sugeridas</h5>
                        <p>{assessment.recommendations}</p>
                    </div>
                )}
            </Card>
        </div>
    );
}
